import { z } from "zod";
import {
  AI_ERROR_CODES,
  STOP_REASONS,
  modelIdSchema,
  providerIdSchema,
  toolArgsSchema,
  usageSchema,
  uuidSchema,
} from "./aiTypes.ts";
import { changeSetAgentEvents } from "./changesets.ts";
import { commandAgentEvents } from "./commands.ts";
import { event } from "./defs.ts";

const base = { runId: uuidSchema, sessionId: uuidSchema };
const toolBase = { toolCallId: z.string().min(1).max(128), name: z.string().min(1).max(64) };

/** Streaming events: agent → server (persist) → browser. */
export const aiEvents = {
  "agent/response.started": event(
    "agent",
    "response.started",
    z.strictObject({ ...base, provider: providerIdSchema, model: modelIdSchema }),
  ),
  "agent/response.delta": event(
    "agent",
    "response.delta",
    /** `offset` = answer characters before this delta, so replays after a reconnect are idempotent. */
    z.strictObject({ ...base, offset: z.number().int().nonnegative(), text: z.string().max(65_536) }),
  ),
  "agent/tool.started": event(
    "agent",
    "tool.started",
    z.strictObject({
      ...base,
      ...toolBase,
      args: toolArgsSchema,
      iteration: z.number().int().nonnegative(),
    }),
  ),
  "agent/tool.completed": event(
    "agent",
    "tool.completed",
    z.strictObject({
      ...base,
      ...toolBase,
      ok: z.boolean(),
      errorCode: z.string().max(64).optional(),
      summary: z.string().max(300),
      durationMs: z.number().int().nonnegative(),
    }),
  ),
  "agent/response.completed": event(
    "agent",
    "response.completed",
    z.strictObject({
      ...base,
      stopReason: z.enum(STOP_REASONS),
      usage: usageSchema.nullable(),
      iterations: z.number().int().nonnegative(),
    }),
  ),
  "agent/response.error": event(
    "agent",
    "response.error",
    z.strictObject({
      ...base,
      code: z.enum(AI_ERROR_CODES),
      message: z.string().max(500),
      retryable: z.boolean(),
    }),
  ),
  "agent/ai.sync": event(
    "agent",
    "ai.sync",
    z.strictObject({
      activeRunIds: z.array(uuidSchema),
    }),
  ),

} as const;

export type AiEventKey = keyof typeof aiEvents;
export type AiEventPayload<K extends AiEventKey> = z.infer<(typeof aiEvents)[K]["payload"]>;

/** Everything an agent may emit during a run: streaming events plus ChangeSet lifecycle events. */
type AllAgentEvents = typeof aiEvents & typeof changeSetAgentEvents & typeof commandAgentEvents;
export type AgentRunEventKey = keyof AllAgentEvents;
export type AgentRunEventPayload<K extends AgentRunEventKey> = z.infer<AllAgentEvents[K]["payload"]>;

/** Discriminated, validated view of an AI event for UIs and the server's persistence layer. */
export type AiEvent = { [K in AiEventKey]: { type: K; payload: AiEventPayload<K> } }[AiEventKey];

export function parseAiEvent(msg: { channel: string; type: string; payload: unknown }): AiEvent | null {
  const key = `${msg.channel}/${msg.type}` as AiEventKey;
  if (!Object.prototype.hasOwnProperty.call(aiEvents, key)) return null;
  const parsed = aiEvents[key].payload.safeParse(msg.payload);
  return parsed.success ? ({ type: key, payload: parsed.data } as AiEvent) : null;
}

/** True for AI streaming events. The relay never forwards these generically; only the chat service may. */
export function isAiEventType(channel: string, type: string): boolean {
  const key = `${channel}/${type}`;
  return (
    Object.prototype.hasOwnProperty.call(aiEvents, key) ||
    Object.prototype.hasOwnProperty.call(changeSetAgentEvents, key) ||
    Object.prototype.hasOwnProperty.call(commandAgentEvents, key)
  );
}
